import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, BarChart3, TrendingUp, FileText } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export const PerformanceTracking = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<any>(null);

  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('seo-automation', {
        body: {
          action: 'generate_report',
          data: {}
        }
      });

      if (error) throw error;

      setReport(data);
      toast({
        title: 'Report Generated',
        description: 'SEO performance report is ready',
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate report',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Performance Tracking & Reporting
          </CardTitle>
          <CardDescription>
            Track keyword rankings, traffic, and generate automated SEO reports
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button onClick={handleGenerateReport} disabled={loading} className="w-full">
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating Report...
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 mr-2" />
                Generate SEO Report
              </>
            )}
          </Button>

          {report && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Tracked Keywords
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{report.metrics.keywords}</div>
                    <p className="text-xs text-muted-foreground">Active keywords</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Audits Run</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{report.metrics.audits}</div>
                    <p className="text-xs text-muted-foreground">Total audits</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Content Briefs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{report.metrics.briefs}</div>
                    <p className="text-xs text-muted-foreground">Generated briefs</p>
                  </CardContent>
                </Card>
              </div>

              <Alert>
                <FileText className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-semibold">SEO Performance Report</p>
                    <div className="text-sm whitespace-pre-wrap">
                      {report.report}
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};